import { create } from 'zustand';
import {
  type Score,
  type EditorState,
  type ClipboardData,
  type Timeline,
  initialEditorState,
  buildTimeline,
  demoScore,
  cursorSoundingMidis,
} from '@simplenotation/core';
import { durationName, pitchName } from '@simplenotation/theory';
import type { NoteNameSystem } from '@simplenotation/theory';
import type { LayoutMode } from '../engrave/types';
import { type History, newHistory, pushEntry, unsavedCount } from './history';
import { loadJson, saveJson } from './persist';
import { noteErrorMessage } from '../app/errorLog';

// ---------------------------------------------------------------- preferences

export interface ViewPrefs {
  layout: LayoutMode;
  zoom: number;
  noteNames: NoteNameSystem;
  noteColors: 'hide' | 'authored' | 'boomwhackers';
  showFretboard: boolean;
  showPiano: boolean;
  leftHanded: boolean;
  touchInput: boolean;
  /** View-only transposition (semitones) — disabled while editing. */
  viewTranspose: number;
  volume: number;
  latencyMs: number;
  playheadStyle: 'line' | 'bar';
  followPlayhead: boolean;
  showTitle: boolean;
}

export interface EditorPrefs {
  playOnSelect: 'none' | 'note' | 'chord';
  playOnInput: 'none' | 'note' | 'chord';
  midiInput: boolean;
  keymap: 'default' | 'finale' | 'guitarpro' | 'sibelius' | 'notebook';
  customKeys: Record<string, string[]>;
}

export interface PlaybackPrefs {
  speed: number; // 0.25 … 2
  metronome: { enabled: boolean; volume: number; frequency: 'beat' | 'bar' };
  countIn: { enabled: boolean; bars: number };
}

const DEFAULT_VIEW: ViewPrefs = {
  layout: 'scroll',
  zoom: 1,
  noteNames: 'none',
  noteColors: 'authored',
  // Open by default, except on phones where it would take half the screen.
  showFretboard: typeof matchMedia === 'undefined' || !matchMedia('(max-width: 600px)').matches,
  showPiano: false,
  leftHanded: false,
  touchInput: false,
  viewTranspose: 0,
  volume: 0.8,
  latencyMs: 0,
  playheadStyle: 'line',
  followPlayhead: true,
  showTitle: true,
};

const DEFAULT_EDITOR_PREFS: EditorPrefs = {
  playOnSelect: 'chord',
  playOnInput: 'note',
  midiInput: false,
  keymap: 'default',
  customKeys: {},
};

const DEFAULT_PLAYBACK: PlaybackPrefs = {
  speed: 1,
  metronome: { enabled: false, volume: 0.7, frequency: 'beat' },
  countIn: { enabled: false, bars: 1 },
};

// ---------------------------------------------------------------- UI state

export type ModalKind =
  | 'transpose'
  | 'keysig'
  | 'timesig'
  | 'clef'
  | 'tempo'
  | 'tuplet'
  | 'endings'
  | 'direction'
  | 'chord'
  | 'text'
  | 'lyrics'
  | 'section'
  | 'multirest'
  | 'barnumber'
  | 'fingering'
  | 'color'
  | 'instrument'
  | 'instruments'
  | 'addInstrument'
  | 'details'
  | 'versions'
  | 'prefs'
  | 'shortcuts'
  | 'export'
  | 'commentary'
  | 'sticking'
  | 'roman'
  | 'bend'
  | 'login'
  | 'bugReport';

export interface Modal {
  kind: ModalKind;
  props?: Record<string, unknown>;
}

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error' | 'success';
}

export interface User {
  id: string;
  email: string | null;
  name: string;
  guest: boolean;
}

export interface DocState {
  slug: string | null;
  version: number | null;
  privacy: 'private' | 'unlisted' | 'public';
  canEdit: boolean;
  ownerName?: string;
  status: 'idle' | 'loading' | 'saving' | 'saved' | 'error' | 'conflict';
  error?: string;
}

export interface PlaybackState {
  playing: boolean;
  /** Played tick (timeline position) used by the playhead. */
  tick: number;
  /** Loop range as played ticks plus the bar range it was made from. */
  loop: { startTick: number; endTick: number; fromBar: number; toBar: number } | null;
  countingIn: boolean;
}

export interface AppState {
  ed: EditorState;
  history: History;
  timeline: Timeline;
  doc: DocState;
  user: User | null;
  view: ViewPrefs;
  editorPrefs: EditorPrefs;
  playbackPrefs: PlaybackPrefs;
  playback: PlaybackState;
  mode: 'edit' | 'view';
  ribbonTab: string;
  ribbonOpen: boolean;
  modal: Modal | null;
  searchOpen: boolean;
  settingsOpen: boolean;
  metronomeOpen: boolean;
  tourStep: number | null;
  recent: string[];
  toasts: Toast[];
  announce: string;
  clipboard: ClipboardData | null;
  /** Monotonic counter bumped when the score view should scroll the cursor into view. */
  revealCursor: number;
}

const savedView = loadJson<Partial<ViewPrefs>>('fl.view', {});
const savedEditorPrefs = loadJson<Partial<EditorPrefs>>('fl.editorPrefs', {});
const savedPlayback = loadJson<Partial<PlaybackPrefs>>('fl.playback', {});

function freshEditor(score: Score): Pick<AppState, 'ed' | 'history' | 'timeline'> {
  const ed = initialEditorState(score);
  return { ed, history: newHistory(score, { cursor: ed.cursor, selection: ed.selection }), timeline: buildTimeline(score) };
}

export const useStore = create<AppState>(() => ({
  ...freshEditor(demoScore()),
  doc: { slug: null, version: null, privacy: 'private', canEdit: true, status: 'idle' },
  user: null,
  view: { ...DEFAULT_VIEW, ...savedView },
  editorPrefs: { ...DEFAULT_EDITOR_PREFS, ...savedEditorPrefs },
  playbackPrefs: { ...DEFAULT_PLAYBACK, ...savedPlayback, metronome: { ...DEFAULT_PLAYBACK.metronome, ...savedPlayback.metronome }, countIn: { ...DEFAULT_PLAYBACK.countIn, ...savedPlayback.countIn } },
  playback: { playing: false, tick: 0, loop: null, countingIn: false },
  mode: 'edit',
  ribbonTab: 'notes',
  ribbonOpen: true,
  modal: null,
  searchOpen: false,
  settingsOpen: false,
  metronomeOpen: false,
  tourStep: null,
  recent: loadJson<string[]>('fl.recent', []),
  toasts: [],
  announce: '',
  clipboard: null,
  revealCursor: 0,
}));

export const getState = useStore.getState;
export const setState = useStore.setState;

// ---------------------------------------------------------------- side-effect hooks (wired by the app)

type Listener = (prev: AppState, next: AppState) => void;
const editListeners = new Set<(ed: EditorState, kind: 'input' | 'select') => void>();
export function onEditorFeedback(cb: (ed: EditorState, kind: 'input' | 'select') => void): () => void {
  editListeners.add(cb);
  return () => editListeners.delete(cb);
}

// ---------------------------------------------------------------- actions

let toastId = 1;
export function toast(text: string, kind: Toast['kind'] = 'info', ms = 3500): void {
  const id = toastId++;
  if (kind === 'error') noteErrorMessage(text);
  setState((s) => ({ toasts: [...s.toasts.slice(-3), { id, text, kind }] }));
  setTimeout(() => setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), ms);
}

export interface EditOptions {
  label?: string;
  /** Edits with the same key within 600 ms merge into one history entry. */
  coalesce?: string;
  /** Merge window for `coalesce` (a mouse drag uses Infinity: one drag, one undo step). */
  coalesceMs?: number;
  /** Treat as a pure selection change (play-on-select feedback). */
  select?: boolean;
  /** Whether the edit came from note input (play-on-input feedback). */
  input?: boolean;
}

/** Apply an editor transition; records history when the score changed. */
export function edit(fn: (ed: EditorState) => EditorState, opts: EditOptions = {}): void {
  const s = getState();
  if (s.mode === 'view' || !s.doc.canEdit) {
    if (fn.length) {
      // Navigation is still allowed in view mode; edits are not.
    }
  }
  const prev = s.ed;
  let next: EditorState;
  try {
    next = fn(prev);
  } catch (e) {
    console.error(e);
    toast(`Something went wrong: ${(e as Error).message}`, 'error');
    return;
  }
  if (next === prev) return;
  if (next.error && next.error !== prev.error) toast(next.error, 'error');
  const changed = next.score !== prev.score;
  if (changed && (!s.doc.canEdit || s.mode === 'view')) {
    toast(s.doc.canEdit ? 'Switch to edit mode to change the notation.' : 'This score is read-only. Duplicate it to edit.', 'error');
    return;
  }
  const patch: Partial<AppState> = { ed: { ...next, error: undefined }, announce: describeCursor(next) };
  if (changed) {
    patch.history = pushEntry(s.history, {
      score: next.score,
      label: opts.label ?? 'Edit',
      before: { cursor: prev.cursor, selection: prev.selection },
      after: { cursor: next.cursor, selection: next.selection },
      coalesce: opts.coalesce,
      time: performance.now(),
    }, opts.coalesceMs);
    patch.timeline = buildTimeline(next.score);
  }
  setState(patch);
  if (changed && opts.input !== false) for (const l of editListeners) l(next, 'input');
  else if (!changed && (next.cursor.beat !== prev.cursor.beat || next.cursor.bar !== prev.cursor.bar || next.cursor.track !== prev.cursor.track)) for (const l of editListeners) l(next, 'select');
}

export function undo(): void {
  const s = getState();
  const h = s.history;
  if (h.index === 0) return;
  const cur = h.entries[h.index];
  const prev = h.entries[h.index - 1];
  const ed = { ...s.ed, score: prev.score, cursor: clampCursor(prev.score, cur.before.cursor), selection: cur.before.selection, error: undefined };
  setState({ ed, history: { ...h, index: h.index - 1 }, timeline: buildTimeline(prev.score), announce: `Undo ${cur.label}` });
}

export function redo(): void {
  const s = getState();
  const h = s.history;
  if (h.index >= h.entries.length - 1) return;
  const next = h.entries[h.index + 1];
  const ed = { ...s.ed, score: next.score, cursor: clampCursor(next.score, next.after.cursor), selection: next.after.selection, error: undefined };
  setState({ ed, history: { ...h, index: h.index + 1 }, timeline: buildTimeline(next.score), announce: `Redo ${next.label}` });
}

function clampCursor(score: Score, c: EditorState['cursor']): EditorState['cursor'] {
  const track = Math.min(c.track, score.tracks.length - 1);
  const bar = Math.min(c.bar, score.bars.length - 1);
  const beats = score.tracks[track].bars[bar].voices[c.voice] ?? [];
  return { ...c, track, bar, beat: Math.min(c.beat, Math.max(0, beats.length - (c.voice === 0 ? 1 : 0))) };
}

/** Replace the whole document (open/import/new). */
export function loadScore(score: Score, doc?: Partial<DocState>): void {
  setState((s) => ({
    ...freshEditor(score),
    doc: { ...s.doc, slug: null, version: null, canEdit: true, privacy: 'private', status: 'idle', error: undefined, ...doc },
    playback: { ...s.playback, playing: false, tick: 0, loop: null },
    modal: null,
  }));
}

export function markSaved(version: number, slug?: string): void {
  setState((s) => ({
    history: { ...s.history, savedIndex: s.history.index },
    doc: { ...s.doc, version, slug: slug ?? s.doc.slug, status: 'saved', error: undefined },
  }));
}

export function unsaved(): number {
  return unsavedCount(getState().history);
}

export function setView(patch: Partial<ViewPrefs>): void {
  setState((s) => {
    const view = { ...s.view, ...patch };
    saveJson('fl.view', view);
    return { view };
  });
}

export function setEditorPrefs(patch: Partial<EditorPrefs>): void {
  setState((s) => {
    const editorPrefs = { ...s.editorPrefs, ...patch };
    saveJson('fl.editorPrefs', editorPrefs);
    return { editorPrefs };
  });
}

export function setPlaybackPrefs(patch: Partial<PlaybackPrefs>): void {
  setState((s) => {
    const playbackPrefs = { ...s.playbackPrefs, ...patch };
    saveJson('fl.playback', playbackPrefs);
    return { playbackPrefs };
  });
}

export function openModal(kind: ModalKind, props?: Record<string, unknown>): void {
  setState({ modal: { kind, props }, searchOpen: false, settingsOpen: false, metronomeOpen: false });
}

export function closeModal(): void {
  setState({ modal: null });
}

export function noteRecent(id: string): void {
  setState((s) => {
    const recent = [id, ...s.recent.filter((r) => r !== id)].slice(0, 8);
    saveJson('fl.recent', recent);
    return { recent };
  });
}

/** Screen-reader description of the cursor: "Bar 2, beat 3, E5 quarter." */
export function describeCursor(ed: EditorState): string {
  const c = ed.cursor;
  const beats = ed.score.tracks[c.track]?.bars[c.bar]?.voices[c.voice] ?? [];
  const b = beats[c.beat];
  let onset = 0;
  for (let i = 0; i < c.beat && i < beats.length; i++) onset += beats[i].grace ? 0 : (3840 / beats[i].duration.base) * (2 - 1 / 2 ** beats[i].duration.dots);
  const beatNum = Math.floor(onset / 960) + 1;
  const what = !b ? 'empty' : b.rest ? `${durationName(b.duration)} rest` : `${b.notes.map((n) => pitchName(n.pitch)).join(' ')} ${durationName(b.duration)}`;
  return `Bar ${c.bar + 1}, beat ${beatNum}, ${what}.`;
}

export function cursorMidis(): number[] {
  return cursorSoundingMidis(getState().ed);
}

export type { Listener };
